import React, { useContext, useEffect, useState } from 'react';
import {
  IDRWidgetCustomisation,
  ISTWWidgetCustomisation,
  ISTWWidgetLeadGeneration,
  IUserInputs,
  WIDGET_NAME,
  LANGUAGE_VALUES,
} from '@bikdotai/bik-models/dm';
import { CountriesData } from '../../widgets/STW/utility';
import { STWContext } from '../../widgets/STW/context';
import useIsMobile from '../../hooks/useIsMobile';
import { ComponentStyleMap } from '../../utilities/styleUtils';
import parse from 'html-react-parser';
import { decodeTextHelper } from '../../widgets/utility';
import EmailInput from '../EmailInput/emailInput';
import SMSInput from '../SmsInput/smsInput';
import WhatsappInputField from '../WhatsappInput/whatsappInput';
import SVGFactory, { SVGName } from '../../widgets/STW/components/svgFactory';
import { phone } from 'phone';
import { tlds } from '@hapi/tlds';
import { LanguageUtils } from '../../utilities/languageUtilities';

export interface IUserFieldDetails {
  value: string | null | boolean;
  countryCodeData?: {
    dial: string;
    code: string;
    code3: string;
  };
  showComponent?: boolean;
  error: string | null;
}

export interface IUserFields {
  isWhatsapp: IUserFieldDetails;
  isSms: IUserFieldDetails;
  isEmail: IUserFieldDetails;
  isPolicyChecked: IUserFieldDetails;
}

const UserDetailsForm: React.FC<{
  claimPrizeHandle?: (userInputs: IUserFields) => Promise<void>;
  widgetCustomisation: ISTWWidgetCustomisation | IDRWidgetCustomisation;
  leadGeneration: ISTWWidgetLeadGeneration;
  error: string;
  isLoading: boolean;
  templateName: WIDGET_NAME;
  showTitle?: boolean;
  skipSubtitle?: boolean;
  widgetName: string;
  countryCode: string;
  id?: string;
  hideCreatorLabel?: boolean;
  widgetLanguage?: LANGUAGE_VALUES;
}> = ({
  widgetCustomisation,
  leadGeneration,
  hideCreatorLabel,
  error,
  isLoading,
  claimPrizeHandle,
  templateName,
  showTitle = true,
  skipSubtitle = false,
  widgetName,
  countryCode,
  id,
  widgetLanguage,
}) => {
  const languageUtilities = new LanguageUtils();
  const skipLabel: boolean = widgetCustomisation.skipLabel ?? false;
  const skipCountryPicker: boolean =
    widgetCustomisation.skipCountryPicker ?? false;
  const selectedCountry = CountriesData.filter(
    country => country.code3 === countryCode,
  );
  const { preview, isMobile } = useContext(STWContext);
  const { fontFamily, fontColour } =
    widgetCustomisation as ISTWWidgetCustomisation;
  const {
    mandatoryOption,
    optionalOptions,
    enableConsentPolicies,
    privacyPolicyURL,
  } = leadGeneration as ISTWWidgetLeadGeneration;
  const isMobileDevice = useIsMobile();
  let userDetailsTitleSize: string;
  let userDetailsSubTitleSize: string;

  if (preview) {
    userDetailsTitleSize = isMobile ? '14px' : '10px';
    userDetailsSubTitleSize = isMobile ? '11px' : '8px';
  } else {
    userDetailsTitleSize = isMobileDevice ? '18px' : `24px`;
    userDetailsSubTitleSize = isMobileDevice ? '14px' : `16px`;
  }

  const [userInputs, setUserInputs] = useState<IUserFields>({
    isWhatsapp: {
      value: null,
      countryCodeData: selectedCountry.length
        ? selectedCountry[0]
        : {
            dial: '+91',
            code: 'IN',
            code3: 'IND',
          },
      showComponent: true,
      error: null,
    },
    isSms: {
      value: null,
      countryCodeData: selectedCountry.length
        ? selectedCountry[0]
        : {
            dial: '+91',
            code: 'IN',
            code3: 'IND',
          },
      showComponent: true,
      error: null,
    },
    isEmail: {
      value: null,
      error: null,
    },
    isPolicyChecked: {
      value: null,
      error: null,
    },
  });
  const [optionlOptionsRevised, setOptionalOptionsRevised] =
    useState<IUserInputs>();
  const [isPrivacyPolicyChecked, setIsPrivacyPolicyChecked] =
    useState<boolean>(false);
  const [currentStyle, setCurrentStyle] = useState<any>(null);
  const multiLingualData =
    widgetCustomisation?.multiLingualConfigurations?.[widgetLanguage];

  useEffect(() => {
    const mandatoryOptionTemp = Object.keys(mandatoryOption).filter(key => {
      return key === 'isWhatsapp' || key === 'isSms';
    });
    if (mandatoryOptionTemp.length) {
      let optionalOptionsTemp = { ...optionalOptions };
      Object.keys(optionalOptions).filter(key => {
        if ((key === 'isWhatsapp' || key === 'isSms') && optionalOptions[key]) {
          optionalOptionsTemp = {
            [key]: optionalOptions[key],
            ...optionalOptionsTemp,
          };
        }
      });
      setOptionalOptionsRevised(optionalOptionsTemp);
    }
  }, [mandatoryOption, optionalOptions]);

  const onChangeHandle = (
    value: string,
    key: 'isWhatsapp' | 'isSms' | 'isEmail',
  ) => {
    setUserInputs({
      ...userInputs,
      [key]: { ...userInputs[key], error: null, value: value },
    });
  };

  const onBlur = (key: 'isWhatsapp' | 'isSms' | 'isEmail') => {
    if (
      key === 'isEmail' &&
      userInputs.isEmail.value &&
      !validateEmail(userInputs.isEmail.value)
    ) {
      setUserInputs({
        ...userInputs,
        isEmail: {
          ...userInputs.isEmail,
          error: languageUtilities.getEmailValidationMessage(widgetLanguage),
        },
      });
    }
    if (
      userInputs.isWhatsapp.value &&
      !phone(
        (userInputs.isWhatsapp.countryCodeData.dial +
          userInputs.isWhatsapp.value) as string,
        { strictDetection: true },
      ).isValid
    ) {
      setUserInputs({
        ...userInputs,
        isWhatsapp: {
          ...userInputs.isWhatsapp,
          error:
            languageUtilities.getPhoneNumberValidationMessage(widgetLanguage),
        },
      });
    }
    if (
      userInputs.isSms.value &&
      !phone(
        (userInputs.isSms.countryCodeData.dial +
          userInputs.isSms.value) as string,
        { strictDetection: true },
      ).isValid
    ) {
      setUserInputs({
        ...userInputs,
        isSms: {
          ...userInputs.isSms,
          error:
            languageUtilities.getPhoneNumberValidationMessage(widgetLanguage),
        },
      });
    }
  };

  const validateEmail = email => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const match = email.match(emailRegex);
    if (!match) return false;

    const domain = email.split('@')[1];
    const tld = domain.split('.').pop();
    return tlds.has(tld);
  };

  const claimPrize = () => {
    let hasError = false;
    let userInputsTemp = { ...userInputs };
    Object.keys(mandatoryOption).map(key => {
      if (mandatoryOption[key] && !userInputs[key].value) {
        userInputsTemp = {
          ...userInputsTemp,
          [key]: {
            ...userInputsTemp[key],
            error: languageUtilities.getRequiredFieldMessage(widgetLanguage),
          },
        };
        hasError = true;
      }
    });
    Object.keys(optionalOptions).map(key => {
      const otherKey = key === 'isWhatsapp' ? 'isSms' : 'isWhatsapp';
      if (
        optionalOptions[key] &&
        key !== 'isEmail' &&
        !userInputs[key].showComponent
      ) {
        userInputsTemp = {
          ...userInputsTemp,
          [key]: {
            ...userInputsTemp[key],
            value: userInputsTemp[otherKey].value,
          },
        };
      }
    });
    if (enableConsentPolicies && !isPrivacyPolicyChecked) {
      hasError = true;
      userInputsTemp = {
        ...userInputsTemp,
        isPolicyChecked: {
          ...userInputsTemp.isPolicyChecked,
          error:
            languageUtilities.getPrivacyPolicyAcceptanceMessage(widgetLanguage),
        },
      };
    }
    if (userInputs.isEmail.value && !validateEmail(userInputs.isEmail.value)) {
      userInputsTemp = {
        ...userInputsTemp,
        isEmail: {
          ...userInputsTemp.isEmail,
          error: languageUtilities.getEmailValidationMessage(widgetLanguage),
        },
      };
      hasError = true;
    }
    if (
      userInputs.isWhatsapp.value &&
      !phone(
        (userInputs.isWhatsapp.countryCodeData.dial +
          userInputs.isWhatsapp.value) as string,
        { strictDetection: true },
      ).isValid
    ) {
      userInputsTemp = {
        ...userInputsTemp,
        isWhatsapp: {
          ...userInputsTemp.isWhatsapp,
          error:
            languageUtilities.getPhoneNumberValidationMessage(widgetLanguage),
        },
      };
      hasError = true;
    }
    if (
      userInputs.isSms.value &&
      !phone(
        (userInputs.isSms.countryCodeData.dial +
          userInputs.isSms.value) as string,
        { strictDetection: true },
      ).isValid
    ) {
      userInputsTemp = {
        ...userInputsTemp,
        isSms: {
          ...userInputsTemp.isSms,
          error:
            languageUtilities.getPhoneNumberValidationMessage(widgetLanguage),
        },
      };
      hasError = true;
    }
    setUserInputs(userInputsTemp);
    if (hasError) {
      return;
    }
    claimPrizeHandle(userInputs).then();
  };

  const onPrivacyPolicyChangeHandle = (value: boolean) => {
    setIsPrivacyPolicyChecked(value);
    setUserInputs({
      ...userInputs,
      isPolicyChecked: { value: value, error: null },
    });
  };

  useEffect(() => {
    if (preview) {
      if (isMobile) {
        ComponentStyleMap[templateName]?.userInputPreviewMobile.then(
          async style => {
            setCurrentStyle((await style()).default);
          },
        );
      } else {
        ComponentStyleMap[templateName]?.userInputPreviewDesktop.then(
          async style => {
            setCurrentStyle((await style()).default);
          },
        );
      }
    } else {
      ComponentStyleMap[templateName]?.globalStyle.then(async style => {
        setCurrentStyle((await style()).default);
      });
    }
  }, [preview, isMobile]);

  useEffect(() => {
    if (currentStyle) {
      document
        .getElementById(currentStyle.bikFooter2)
        .setAttribute(
          'dir',
          languageUtilities.getLanguageDirection(widgetLanguage),
        );
    }
  }, [widgetLanguage, currentStyle]);

  const screenTitle =
    (multiLingualData?.postPlayScreenTitle ??
    widgetCustomisation?.postPlayScreenTitle) ? (
      parse(
        decodeTextHelper(
          multiLingualData?.postPlayScreenTitle ??
            widgetCustomisation.postPlayScreenTitle,
        ),
      )
    ) : (
      <p>
        <strong>Special prize only for you!</strong>
      </p>
    );
  const screenSubTitle =
    (multiLingualData?.postPlayScreenSubTitle ??
    widgetCustomisation?.postPlayScreenSubTitle) ? (
      parse(
        decodeTextHelper(
          multiLingualData?.postPlayScreenSubTitle ??
            widgetCustomisation.postPlayScreenSubTitle,
        ),
      )
    ) : (
      <></>
    );
  const screenButtonText =
    templateName === WIDGET_NAME.DIRECT_REWARD ||
    templateName === WIDGET_NAME.LEAD_GENERATOR
      ? (multiLingualData?.playScreenButtonText ??
        widgetCustomisation?.playScreenButtonText)
      : (multiLingualData?.postPlayScreenButtonText ??
        widgetCustomisation.postPlayScreenButtonText ??
        'Claim the price');
  const screenButtonTextColour =
    templateName === WIDGET_NAME.DIRECT_REWARD ||
    templateName === WIDGET_NAME.LEAD_GENERATOR
      ? (widgetCustomisation as IDRWidgetCustomisation)
          .playScreenButtonTextColour
      : (widgetCustomisation.postPlayScreenButtonTextColour ?? '#fff');
  const screenButtonColour =
    templateName === WIDGET_NAME.DIRECT_REWARD ||
    templateName === WIDGET_NAME.LEAD_GENERATOR
      ? (widgetCustomisation as IDRWidgetCustomisation).playScreenButtonColour
      : (widgetCustomisation.postPlayScreenButtonColour ?? '#731dcf');

  if (!currentStyle) {
    return <></>;
  }

  return (
    <>
      <div
        id={currentStyle.bikFooter2}
        style={{
          fontFamily: fontFamily?.name,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showTitle && (
          <div
            style={{ fontSize: userDetailsTitleSize, color: fontColour }}
            className={currentStyle.userDetailsTitleWrapper}
          >
            {screenTitle}
          </div>
        )}
        {!skipSubtitle && (
          <div
            style={{
              marginTop: preview ? 4 : 8,
              fontSize: userDetailsSubTitleSize,
              color: fontColour,
              opacity: 0.8,
            }}
            className={currentStyle.userDetailsTitleWrapper}
          >
            {screenSubTitle}
          </div>
        )}
        {Object.keys(mandatoryOption).map(key => {
          return (
            <>
              {key === 'isEmail' && mandatoryOption[key] && (
                <EmailInput
                  id={`bik-${id}-input-email`}
                  onChangeHandle={onChangeHandle}
                  value={userInputs.isEmail.value as string}
                  error={userInputs.isEmail.error}
                  skipLabel={skipLabel ?? false}
                  fontColour={fontColour}
                  onBlur={onBlur}
                  widgetLanguage={widgetLanguage}
                />
              )}
              {key === 'isSms' && mandatoryOption[key] && (
                <SMSInput
                  id={`bik-${id}-input-sms`}
                  selectedCountryData={userInputs.isSms.countryCodeData}
                  setUserInputs={setUserInputs}
                  onChangeHandle={onChangeHandle}
                  value={userInputs.isSms.value as string}
                  error={userInputs.isSms.error}
                  skipLabel={skipLabel ?? false}
                  skipCountryPicker={skipCountryPicker ?? false}
                  fontColour={fontColour}
                  onBlur={onBlur}
                  widgetLanguage={widgetLanguage}
                />
              )}
              {key === 'isWhatsapp' && mandatoryOption[key] && (
                <WhatsappInputField
                  id={`bik-${id}-input-whatsapp`}
                  selectedCountryData={userInputs.isWhatsapp.countryCodeData}
                  setUserInputs={setUserInputs}
                  onChangeHandle={onChangeHandle}
                  value={userInputs.isWhatsapp.value as string}
                  error={userInputs.isWhatsapp.error}
                  skipLabel={skipLabel ?? false}
                  skipCountryPicker={skipCountryPicker ?? false}
                  fontColour={fontColour}
                  onBlur={onBlur}
                  screenButtonColour={screenButtonColour}
                  widgetLanguage={widgetLanguage}
                />
              )}
            </>
          );
        })}
        {optionlOptionsRevised &&
          Object.keys(optionlOptionsRevised)?.map(key => {
            return (
              <>
                {key === 'isEmail' && optionlOptionsRevised[key] && (
                  <EmailInput
                    fontColour={fontColour}
                    id={`bik-${id}-input-email-op`}
                    onChangeHandle={onChangeHandle}
                    value={userInputs.isEmail.value as string}
                    title={languageUtilities.getOptionalEmailTitle(
                      widgetLanguage,
                    )}
                    error={userInputs.isEmail.error}
                    skipLabel={skipLabel ?? false}
                    onBlur={onBlur}
                    widgetLanguage={widgetLanguage}
                  />
                )}
                {key === 'isSms' &&
                  optionlOptionsRevised[key] &&
                  userInputs.isSms.showComponent && (
                    <SMSInput
                      fontColour={fontColour}
                      id={`bik-${id}-input-sms-op`}
                      selectedCountryData={userInputs.isSms.countryCodeData}
                      setUserInputs={setUserInputs}
                      onChangeHandle={onChangeHandle}
                      value={userInputs.isSms.value as string}
                      title={languageUtilities.getOptionalPhoneNumberTitle(
                        widgetLanguage,
                      )}
                      error={userInputs.isSms.error}
                      skipLabel={skipLabel ?? false}
                      skipCountryPicker={skipCountryPicker ?? false}
                      onBlur={onBlur}
                      widgetLanguage={widgetLanguage}
                    />
                  )}
                {key === 'isWhatsapp' &&
                  optionlOptionsRevised[key] &&
                  userInputs.isWhatsapp.showComponent && (
                    <WhatsappInputField
                      fontColour={fontColour}
                      id={`bik-${id}-input-whatsapp-op`}
                      selectedCountryData={
                        userInputs.isWhatsapp.countryCodeData
                      }
                      setUserInputs={setUserInputs}
                      onChangeHandle={onChangeHandle}
                      value={userInputs.isWhatsapp.value as string}
                      title={languageUtilities.getOptionalWhatsappNumberTitle(
                        widgetLanguage,
                      )}
                      error={userInputs.isWhatsapp.error}
                      skipLabel={skipLabel ?? false}
                      skipCountryPicker={skipCountryPicker ?? false}
                      onBlur={onBlur}
                      widgetLanguage={widgetLanguage}
                      screenButtonColour={screenButtonColour}
                    />
                  )}
              </>
            );
          })}
        {enableConsentPolicies && (
          <>
            {/* Style override for bikStwH2 font size */}
            <style>{`.bikStwH2Override { font-size: ${preview ? '8px' : '12px'} !important; }`}</style>
            <div
              className={currentStyle.bikStwWaCheckboxContainer}
              style={preview ? { gap: '4px', marginTop: '4px' } : {}}
            >
              <input
                type="checkbox"
                id={currentStyle.bikStwPolicyCheckbox}
                name="bik-wa"
                checked={isPrivacyPolicyChecked}
                onChange={e => onPrivacyPolicyChangeHandle(e.target.checked)}
                style={{
                  display: 'block',
                  margin: 0,
                  ...(preview ? { width: '10px' } : {}),
                }}
              />
              <h2
                className={`${currentStyle.bikStwH2} bikStwH2Override`}
                style={{ color: fontColour }}
              >
                {languageUtilities.getPrivacyPolicyText(widgetLanguage)}{' '}
                <a
                  href={privacyPolicyURL}
                  style={{
                    color: fontColour,
                    fontSize: preview ? '8px' : '12px',
                  }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {languageUtilities.getPrivacyPolicyLabel(widgetLanguage)}
                </a>
              </h2>
            </div>
            {userInputs.isPolicyChecked.error?.length ? (
              <p id={currentStyle.bikErrorWrapper}>
                {userInputs.isPolicyChecked.error}
              </p>
            ) : (
              <></>
            )}
          </>
        )}
        {error?.length ? (
          <p id={currentStyle.bikErrorWrapper} style={{ color: fontColour }}>
            {error}
          </p>
        ) : (
          <></>
        )}
        <button
          id={currentStyle.bikayiBtn}
          className={currentStyle.bikCliamBtn}
          onClick={() => claimPrize()}
          style={{
            backgroundColor: screenButtonColour,
            color: screenButtonTextColour,
            fontFamily: fontFamily?.name ?? 'inherit',
          }}
        >
          {isLoading ? (
            <div className={currentStyle.loader}></div>
          ) : (
            screenButtonText
          )}
        </button>
      </div>
      {!widgetCustomisation.hidePoweredBy && !hideCreatorLabel && (
        <p
          className={currentStyle.poweredBy}
          style={{ opacity: 0.4 }}
          onClick={() =>
            window.open(
              `https://bik.ai/?utm_campaign=${widgetName}&utm_medium=Widgets&utm_source=Powered_by_bik`,
            )
          }
        >
          <a
            href={`https://bik.ai/?utm_campaign=${widgetName}&utm_medium=Widgets&utm_source=Powered_by_bik`}
            target="_blank"
            rel="noreferrer"
            style={{ color: fontColour }}
          >
            {languageUtilities.getPoweredByMessage(widgetLanguage)}
          </a>
          <SVGFactory name={SVGName.BIK} color={fontColour} />
        </p>
      )}
    </>
  );
};
export default UserDetailsForm;
